import {
  type RailMenuEntry,
  type RailMenuItem,
  type RailRow,
  type RailRowActionId,
  railRowMenu,
} from '@modules/common/components/rail-menu/rail-menu';
import { type VirtualFolder } from '@modules/tasks/types/virtual-folder';

/** Whose Tasks rail row a menu belongs to: the member's My Workspace or the Team Workspace. */
export type TasksRailScope = 'mine' | 'team';

/** A Tasks rail row's key: the app's virtual folders plus the Shared header, which is not a folder. */
export type TasksRailKey = VirtualFolder | 'shared';

/** Tasks' rail rows by the prototype's names. Rows not listed here keep their own menu. */
export const TASKS_RAIL_ROWS: Readonly<Partial<Record<TasksRailKey, RailRow>>> = {
  all_tasks: 'all',
  all_projects: 'all',
  assigned_to_me: 'assigned',
  shared: 'shared',
  overdue: 'overdue',
  today: 'today',
  flagged: 'flagged',
  followed: 'followup',
  snoozed: 'snoozed',
  following: 'following',
  archived: 'archive',
  deleted: 'trash',
};

export interface TasksRowMenuContext {
  /** Why this person may not empty this Trash; absent when they may. */
  readonly purgeRefused?: string;
  /** A team login: the row offers its workspace's Tasks settings (Team apps or My apps). */
  readonly appSettings?: boolean;
}

const TEAM_CLEAR_HINTS = {
  flagged: 'This removes flags for everyone in the team.',
  followed: 'This clears your follow-up reminders in this view. Other teammates’ reminders are unchanged.',
  snoozed: 'This clears your snoozes in this view. Other teammates’ reminders are unchanged.',
} as const;

const TEAM_CLEAR_TITLES = {
  flagged: 'Unflag all',
  followed: 'Remove all follow-ups',
  snoozed: 'Unsnooze all',
} as const;

export const PINS_SHARED_HINT = 'Pins in this workspace are shared by the team.';

/**
 * A Tasks workspace rail row's menu: the prototype's entries from the shared definition, plus Tasks' own
 * entries kept beside them (additive rule). `undefined` for a row the shared definition does not cover.
 *
 * Trash and Archive reviews stay Coming soon: they touch team projects, which have their own destruction rule.
 */
export function tasksRowMenu(
  key: TasksRailKey,
  scope: TasksRailScope,
  context: TasksRowMenuContext = {},
): readonly RailMenuItem[] | undefined {
  const row = TASKS_RAIL_ROWS[key];
  if (!row) return undefined;
  const team = scope === 'team';

  const live = new Set<RailRowActionId>(['new-item', 'new-container', 'settings']);
  if (team) {
    // Team flags are one shared state: the personal "Clear my flags" has nothing of its own to clear (Coming soon).
    if (key === 'followed' || key === 'snoozed' || key === 'following') live.add('clear');
  } else if (key === 'flagged' || key === 'followed' || key === 'snoozed') {
    live.add('clear');
  }

  const extras: RailMenuEntry[] = [];
  if (team && (key === 'flagged' || key === 'followed' || key === 'snoozed')) {
    extras.push({
      id: 'clear-team',
      title: TEAM_CLEAR_TITLES[key],
      icon: 'sapphire-close',
      hint: TEAM_CLEAR_HINTS[key],
    });
  }
  // My Following has no "Settings" in the old menu either; the team's Following always had one.
  if (key === 'archived') {
    // Coming soon: it would trash every archived item with no confirmation and no bound on the rows.
    extras.push({ id: 'delete-all', title: 'Delete all', icon: 'sapphire-trash', dangerous: true, comingSoon: true });
  }
  if (key === 'deleted') {
    extras.push({
      id: 'empty-trash-all',
      title: 'Empty trash',
      icon: 'sapphire-trash',
      dangerous: true,
      disabled: !!context.purgeRefused,
      reason: context.purgeRefused,
    });
  }

  if (context.appSettings) {
    // The settings of the workspace this row belongs to, worded as Calendar's entry (teamcalendar r1).
    extras.push({
      id: 'app-settings',
      title: 'App settings…',
      icon: 'sapphire-settings',
      hint: team ? 'Team apps › Tasks' : 'My apps › Tasks',
    });
  }

  return railRowMenu({
    row,
    itemNoun: 'task',
    containerNoun: 'project',
    readable: false,
    live,
    extras,
  });
}

/**
 * The menu of the Pinned/Team Knots or Tags row. The prototype's own entry manages the member's personal
 * pins, which a team's shared pins cannot be (Coming soon); Tasks' Add and Manage stay beside it.
 */
export function tasksPinnedMenu(kind: 'knots' | 'tags', scope: TasksRailScope): readonly RailMenuItem[] {
  const team = scope === 'team';
  return [
    { id: 'manage-pins', title: `Manage my pinned ${kind}`, icon: 'sapphire-settings', comingSoon: team },
    null,
    { id: 'add-meta', title: `Add ${kind}`, icon: 'sapphire-plus' },
    {
      id: 'manage-meta',
      title: `Manage ${kind}`,
      icon: 'sapphire-settings',
      hint: team ? PINS_SHARED_HINT : undefined,
    },
  ];
}
